import React, { useRef, useState } from 'react'
import useIsomorphicLayoutEffect from 'hooks/useIsomorphicLayoutEffect'
import classnames from 'classnames/bind'
import delve from 'dlv'
import gsap from 'gsap'

import Img from 'components/shared/Image/Img'
import { Text } from 'components/typo'

import useTimeline from 'hooks/useTimeline'

import PersonNode from './PersonNode'
import mapData from './mapData'

import css from './styles.module.scss'

const cx = classnames.bind(css)

/**
 * Careers Map Documentation -
 * The map supports two types of nodes, "person", and "event".
 *
 * I generated the mapData json from the data the SVG of the map provided,
 * so each node is individual, and marked with a node id.
 * What the component will do is go through each node, and render it in a svg.
 *
 * Among the rendered nodes, the "event" ones will add a class and listeners
 * directly on the targeted node ids.
 * In the case of "person" nodes, they're rendered on top of the original node,
 * with its own svg path (see the PersonNode import).
 *
 * Person nodes only support 1 individual at this time, but could support more.
 */

const nodeType = {
  person: 'person',
  event: 'event',
}

const CareersMap = ({ nodes }) => {
  const [hoveredEvent, setHoveredEvent] = useState({ hover: false, id: undefined })
  const mapRef = useRef()
  const eventBackgroundRef = useRef()
  const eventWrapperRef = useRef()

  const personNodes = nodes.filter(node => node.type === nodeType.person) || []
  const eventNodes = nodes.filter(node => node.type === nodeType.event) || []

  const tl = useTimeline(
    {
      paused: true,
    },
    (tl) => {
      tl.fromTo(
        eventWrapperRef.current,
        {
          autoAlpha: 0,
        },
        {
          autoAlpha: 1,
          duration: 0.25,
        },
        '<'
      ).fromTo(
        eventBackgroundRef.current,
        {
          z: -150,
        },
        {
          z: 0,
          transformPerspective: 1000,
          duration: 0.6,
          force3D: true,
          ease: 'elastic.out(1, 0.75)',
        },
        '<'
      )
    },
    []
  )

  useIsomorphicLayoutEffect(() => {
    gsap.fromTo(mapRef.current, {
        z: -50,
        autoAlpha: 0,
      },
      {
        z: 0,
        autoAlpha: 1,
        transformPerspective: 1000,
        duration: 1.2,
        delay: .5,
        force3D: true,
        ease: 'power3.easeOut',
      },
    )
  }, [])

  useIsomorphicLayoutEffect(() => {
    if (delve(hoveredEvent, 'hover')) {
      tl.timeScale(1)
      tl.play()
    } else {
      tl.timeScale(2)
      tl.reverse()
    }
  }, [hoveredEvent])

  const selectedEvent = eventNodes.find(e => e.nodeId == delve(hoveredEvent, 'id'))
  const eventMediaUrl = delve(selectedEvent, 'media.data.0.attributes.url')

  return (
    <div className={css.CareersMap}>
      <svg
        ref={mapRef}
        width="1079"
        height="700"
        viewBox="0 0 1079 700"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <filter id="mapShadows" x=".7" y=".6" width="1078.2" height="698.7" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feColorMatrix in="SourceAlpha" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" />
            <feOffset dy="3.7" />
            <feGaussianBlur stdDeviation=".4" />
            <feColorMatrix values="0 0 0 0 0.0588235 0 0 0 0 0.0313726 0 0 0 0 0.262745 0 0 0 0.0414311 0" />
            <feBlend in2="BackgroundImageFix" result="effect1_dropShadow" />
            <feColorMatrix in="SourceAlpha" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" />
            <feOffset dy="10.3" />
            <feGaussianBlur stdDeviation="3.9" />
            <feColorMatrix values="0 0 0 0 0.252036 0 0 0 0 0.19566 0 0 0 0 0.670833 0 0 0 0.0458509 0" />
            <feBlend in2="effect1_dropShadow" result="effect2_dropShadow" />
            <feColorMatrix in="SourceAlpha" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" />
            <feOffset dy="24.8" />
            <feGaussianBlur stdDeviation="16.6" />
            <feColorMatrix values="0 0 0 0 0.209938 0 0 0 0 0.141163 0 0 0 0 0.720833 0 0 0 0.0469633 0" />
            <feBlend in2="effect2_dropShadow" result="effect3_dropShadow" />
            <feColorMatrix in="SourceAlpha" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" />
            <feOffset dy="82.2" />
            <feGaussianBlur stdDeviation="27.6" />
            <feColorMatrix values="0 0 0 0 0.220859 0 0 0 0 0.148507 0 0 0 0 0.758333 0 0 0 0.07 0" />
            <feBlend in2="effect3_dropShadow" result="effect4_dropShadow" />
            <feBlend in="SourceGraphic" in2="effect4_dropShadow" result="shape" />
          </filter>
        </defs>
        <g className={css.nodes} filter="url(#mapShadows)">
          {Object.values(mapData).map((path, key) => {
            const nodeId = Object.keys(mapData)[key];
            const isEvent = eventNodes.some(n => delve(n, 'nodeId') == nodeId);

            return (
              <path
                key={nodeId}
                className={cx({ [css.eventNode]: isEvent })}
                data-node-id={nodeId}
                d={delve(path, 'd')}
                {...(isEvent && {
                  onMouseEnter: () => setHoveredEvent({ hover: true, id: nodeId }),
                  onMouseLeave: () => setHoveredEvent({ hover: false, id: nodeId }),
                })}
              />
            )
          })}
        </g>
        <g>
          {personNodes.map(node => <PersonNode key={delve(node, 'nodeId')} node={node} />)}
        </g>
      </svg>

      <div
        ref={eventWrapperRef}
        className={css.eventTooltip}
        style={{ left: `${delve(mapData, `${delve(hoveredEvent, 'id')}.x`, 0)}px`, top: `${delve(mapData, `${delve(hoveredEvent, 'id')}.y`, 0)}px` }}
      >
        <Img
          ref={eventBackgroundRef}
          key={eventMediaUrl}
          className={css.eventBackground}
          src={`${process.env.IMAGE_URL}${eventMediaUrl}`}
          fit="cover"
        />
        <div className={css.eventTooltipContent}>
          <Text className={css.eventName} theme="white" size="small">{delve(selectedEvent, 'name')}</Text>
          <Text className={css.eventText} theme="white" size="smaller">{delve(selectedEvent, 'text')}</Text>
        </div>
      </div>
    </div>
  )
}

CareersMap.defaultProps = {
  nodes: []
}

export default CareersMap
